import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity } from "lucide-react";

const fmtHour = (iso) => new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });

const peakInfo = (points) => {
  if (!points || points.length < 2) return null;
  const peak = points.reduce((a, b) => (b.count > a.count ? b : a));
  const cur = points[points.length - 1];
  const hr = new Date(peak.t).toLocaleTimeString("en-AU", { timeZone: "Australia/Sydney", hour: "numeric", hour12: true }).replace(" ", "").toLowerCase();
  const busyNow = cur.count >= peak.count * 0.85;
  return { hr, busyNow, cur: cur.count, peak: peak.count };
};

const PlayerActivity = ({ hours = 6, compact = false, callout = false }) => {
  const [data, setData] = useState({ points: [], peak: 0 });

  useEffect(() => {
    let alive = true;
    api.get("/server/history", { params: { hours } }).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [hours]);

  const chart = (data.points || []).map((p) => ({ t: p.t, count: p.count, label: fmtHour(p.t) }));
  const info = peakInfo(data.points);

  if (compact) {
    return (
      <div>
        <div data-testid="player-activity-compact" style={{ width: "100%", height: 44, minHeight: 44 }}>
          <ResponsiveContainer width="100%" height={44} minWidth={0}>
            <AreaChart data={chart} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="paCompact" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4A94A" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#D4A94A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="count" stroke="#D4A94A" strokeWidth={1.5} fill="url(#paCompact)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {callout && info && (
          <div data-testid="peak-hours-callout" className="mt-1.5 font-mono text-[11px] text-[#94A3B8] flex items-center gap-2 flex-wrap">
            {info.busyNow
              ? <><span className="inline-flex items-center gap-1 text-[#2ECC71]"><Activity className="w-3 h-3" /> Busy right now</span><span className="text-[#64748B]">· usually peaks around {info.hr} AEST</span></>
              : <><Activity className="w-3 h-3 text-[#D4A94A]" /> Usually busiest around <span className="text-[#F1F5F9]">{info.hr} AEST</span></>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[#13151B] border border-[#2A2E3D] tactical-frame p-5" data-testid="player-activity">
      <div className="flex items-center justify-between mb-3">
        <div className="overline"><Activity className="inline w-3 h-3 mr-1" /> Player Activity · last {hours}h{data.typical ? " · typical" : ""}</div>
        <div className="font-mono text-xs text-[#94A3B8]">peak <span className="text-[#D4A94A] font-heading font-black">{data.peak}</span></div>
      </div>
      <div style={{ width: "100%", height: 160, minHeight: 160 }}>
        <ResponsiveContainer width="100%" height={160} minWidth={0}>
          <AreaChart data={chart} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="paFull" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4A94A" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#D4A94A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} stroke="#2A2E3D" minTickGap={40} />
            <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} stroke="#2A2E3D" domain={[0, 100]} width={40} />
            <Tooltip contentStyle={{ background: "#0B0C0E", border: "1px solid #2A2E3D", fontFamily: "monospace", fontSize: 12 }} labelStyle={{ color: "#D4A94A" }} formatter={(v) => [`${v} operators`, "Players"]} />
            <Area type="monotone" dataKey="count" stroke="#D4A94A" strokeWidth={2} fill="url(#paFull)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="font-mono text-[10px] text-[#64748B] mt-2">Spot peak OCE hours before you jump in.</p>
    </div>
  );
};

export default PlayerActivity;
